import type { ActivityFilters } from '../types/activity';
import type { ScheduledActivitiesQueryParams } from '../types/scheduledActivity';
import type { StrategyFilters } from '../types/strategy';
import type { ExpertProfileFilters } from '../types/expert-profile';
import type { ExpertTipFilters } from '../types/expert-tip';
import type { ExpertTipTopicFilters } from '../types/expert-tip-topic';
import type { EarlyAccessFilters } from '../types/early-access';

/**
 * Query keys factory for React Query
 * Provides a consistent, hierarchical structure for cache keys
 */
export const queryKeys = {
  dashboard: {
    all: ['dashboard'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const,
  },
  analytics: {
    all: ['analytics'] as const,
    stats: (dateRange: { dateFrom?: string; dateTo?: string }) =>
      [...queryKeys.analytics.all, 'stats', dateRange] as const,
    runs: () => [...queryKeys.analytics.all, 'runs'] as const,
    run: (runId: number) => [...queryKeys.analytics.all, 'run', runId] as const,
  },
  activities: {
    all: ['activities'] as const,
    lists: () => [...queryKeys.activities.all, 'list'] as const,
    list: (filters: Partial<ActivityFilters>) =>
      [...queryKeys.activities.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.activities.all, 'detail', id] as const,
    modalList: () => [...queryKeys.activities.all, 'modal'] as const,
  },
  tags: {
    all: ['tags'] as const,
    list: () => [...queryKeys.tags.all, 'list'] as const,
  },
  transitions: {
    all: ['transitions'] as const,
    list: () => [...queryKeys.transitions.all, 'list'] as const,
  },
  developmentAreas: {
    all: ['developmentAreas'] as const,
    list: () => [...queryKeys.developmentAreas.all, 'list'] as const,
  },
  categories: {
    all: ['categories'] as const,
    list: () => [...queryKeys.categories.all, 'list'] as const,
  },
  scheduledActivities: {
    all: ['scheduledActivities'] as const,
    lists: () => [...queryKeys.scheduledActivities.all, 'list'] as const,
    list: (params?: ScheduledActivitiesQueryParams) =>
      [...queryKeys.scheduledActivities.lists(), params] as const,
    byDate: (date: string) =>
      [...queryKeys.scheduledActivities.all, 'byDate', date] as const,
    detail: (id: string) =>
      [...queryKeys.scheduledActivities.all, 'detail', id] as const,
  },
  generationJobs: {
    all: ['generationJobs'] as const,
    lists: () => [...queryKeys.generationJobs.all, 'list'] as const,
    list: (params?: { page?: number; pageSize?: number; status?: string }) =>
      [...queryKeys.generationJobs.lists(), params] as const,
    detail: (id: string) =>
      [...queryKeys.generationJobs.all, 'detail', id] as const,
  },
  // Note: promptPreview is NOT added here — usePreviewPrompt is a useMutation,
  // not a useQuery, so it has no cache key.
  strategies: {
    all: ['strategies'] as const,
    lists: () => [...queryKeys.strategies.all, 'list'] as const,
    list: (filters: Partial<StrategyFilters>) =>
      [...queryKeys.strategies.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.strategies.all, 'detail', id] as const,
  },
  strategyCategories: {
    all: ['strategyCategories'] as const,
    list: () => [...queryKeys.strategyCategories.all, 'list'] as const,
  },
  predefinedImages: {
    all: ['predefinedImages'] as const,
    list: () => [...queryKeys.predefinedImages.all, 'list'] as const,
  },
  predefinedActivityImages: {
    all: ['predefinedActivityImages'] as const,
    list: () => [...queryKeys.predefinedActivityImages.all, 'list'] as const,
  },
  strategyIcons: {
    all: ['strategyIcons'] as const,
    list: () => [...queryKeys.strategyIcons.all, 'list'] as const,
  },
  expertProfiles: {
    all: ['expertProfiles'] as const,
    lists: () => [...queryKeys.expertProfiles.all, 'list'] as const,
    list: (filters: Partial<ExpertProfileFilters>) =>
      [...queryKeys.expertProfiles.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.expertProfiles.all, 'detail', id] as const,
    active: () => [...queryKeys.expertProfiles.all, 'active'] as const,
  },
  expertTips: {
    all: ['expertTips'] as const,
    lists: () => [...queryKeys.expertTips.all, 'list'] as const,
    list: (filters: Partial<ExpertTipFilters>) =>
      [...queryKeys.expertTips.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.expertTips.all, 'detail', id] as const,
  },
  expertTipTopics: {
    all: ['expertTipTopics'] as const,
    lists: () => [...queryKeys.expertTipTopics.all, 'list'] as const,
    list: (filters: Partial<ExpertTipTopicFilters>) =>
      [...queryKeys.expertTipTopics.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.expertTipTopics.all, 'detail', id] as const,
    active: () => [...queryKeys.expertTipTopics.all, 'active'] as const,
  },
  expertTipTopicSchedules: {
    all: ['expertTipTopicSchedules'] as const,
    lists: () => [...queryKeys.expertTipTopicSchedules.all, 'list'] as const,
    list: (params?: { year: number; month: number }) =>
      [...queryKeys.expertTipTopicSchedules.lists(), params] as const,
  },
  adminUsers: {
    all: ['adminUsers'] as const,
    lists: () => [...queryKeys.adminUsers.all, 'list'] as const,
    list: () => [...queryKeys.adminUsers.lists()] as const,
    detail: (id: string) => [...queryKeys.adminUsers.all, 'detail', id] as const,
  },
  adminRecommendations: {
    all: ['adminRecommendations'] as const,
    child: (childId: number) => [...queryKeys.adminRecommendations.all, 'child', childId] as const,
  },
  earlyAccessSignups: {
    all: ['earlyAccessSignups'] as const,
    lists: () => [...queryKeys.earlyAccessSignups.all, 'list'] as const,
    list: (filters: Partial<EarlyAccessFilters>) =>
      [...queryKeys.earlyAccessSignups.lists(), filters] as const,
  },
  communicationStages: {
    all: ['communicationStages'] as const,
    list: () => [...queryKeys.communicationStages.all, 'list'] as const,
    detail: (id: number) => [...queryKeys.communicationStages.all, 'detail', id] as const,
  },
  domains: {
    all: ['domains'] as const,
    list: () => [...queryKeys.domains.all, 'list'] as const,
    detail: (id: number) => [...queryKeys.domains.all, 'detail', id] as const,
  },
  domainSkills: {
    all: ['domainSkills'] as const,
    lists: () => [...queryKeys.domainSkills.all, 'list'] as const,
    list: (filters?: { domainId?: number }) =>
      [...queryKeys.domainSkills.lists(), filters] as const,
    detail: (id: number) => [...queryKeys.domainSkills.all, 'detail', id] as const,
  },
  childPreferences: {
    all: ['childPreferences'] as const,
    list: () => [...queryKeys.childPreferences.all, 'list'] as const,
    detail: (id: number) => [...queryKeys.childPreferences.all, 'detail', id] as const,
  },
  sparkPlays: {
    all: ['sparkPlays'] as const,
    lists: () => [...queryKeys.sparkPlays.all, 'list'] as const,
    list: () => [...queryKeys.sparkPlays.lists()] as const,
    detail: (id: number) => [...queryKeys.sparkPlays.all, 'detail', id] as const,
  },
  activityPreps: {
    all: ['activityPreps'] as const,
    lists: () => [...queryKeys.activityPreps.all, 'list'] as const,
    list: () => [...queryKeys.activityPreps.lists()] as const,
    detail: (id: number) => [...queryKeys.activityPreps.all, 'detail', id] as const,
  },
  packNames: {
    all: ['packNames'] as const,
    list: () => [...queryKeys.packNames.all, 'list'] as const,
  },
  sparkActivities: {
    all: ['sparkActivities'] as const,
    lists: () => [...queryKeys.sparkActivities.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.sparkActivities.lists(), filters] as const,
    detail: (id: number) => [...queryKeys.sparkActivities.all, 'detail', id] as const,
    batches: () => [...queryKeys.sparkActivities.all, 'batches'] as const,
    filters: () => [...queryKeys.sparkActivities.all, 'filters'] as const,
    checkKey: (key: string) => [...queryKeys.sparkActivities.all, 'checkKey', key] as const,
    overview: (status: string) => [...queryKeys.sparkActivities.all, 'overview', status] as const,
  },
} as const;
